import { buildBracketTree, hasConsolation } from "@/utils/league-bracket";
import { goalsBySquadId } from "@/utils/race-utils";

// Maps a league's single-elimination bracket onto the `rounds` shape consumed by
// bracket-viewer via bracket-layout: an array of rounds (chronological, final last), each
// with `races: [{ homeSquad, awaySquad, bracketSlot }]`. Squad objects are
// { id, name, winner, score, placeholder } where winner is true (won), false (lost) or null
// (undecided/by/TBD) and score is the squad's current race total (null when unknown).
// A side may also be a Bye, seed-number or TBD placeholder instead of a real squad, flagged
// with placeholder: true.
// Returns [] when the league has no bracket.
export function bracketRounds (league) {
  const bracketSize = league.bracketSize;
  const squadCount = league.squads.length;
  const actualTeamCount = Math.min(bracketSize, squadCount);
  if (actualTeamCount < 2) return [];

  const tree = buildBracketTree(actualTeamCount);
  const squadById = new Map(league.squads.map(s => [s.id, s]));
  const slotSquads = league.bracketSquads;

  const rounds = [];
  let current = [];
  let lastRound = 0;
  for (const event of tree) {
    const race = buildRace({ event, league, slotSquads, squadById });
    if (event.round !== lastRound) {
      if (current.length) rounds.push({ races: current });
      current = [];
      lastRound = event.round;
    }
    current.push(race);
  }
  if (current.length) rounds.push({ races: current });
  return rounds;
}

// The third-place consolation match, as the same { homeSquad, awaySquad, bracketSlot } shape
// as a normal race, or null when the league has none. It is intentionally excluded from the
// elimination `rounds` array above and surfaced separately so the viewer can draw it detached.
export function bracketConsolation (league) {
  const actualTeamCount = Math.min(league.bracketSize, league.squads.length);
  if (!hasConsolation(actualTeamCount)) return null;
  const squadIds = (league.bracketSquads || {}).THIRD || [undefined, undefined];
  const squadById = new Map(league.squads.map(s => [s.id, s]));
  return buildRace({
    event: { bracketSlot: "THIRD", round: Infinity, homeSeed: null, awaySeed: null },
    league,
    slotSquads: { THIRD: squadIds },
    squadById
  });
}

function buildRace ({ event, league, slotSquads, squadById }) {
  const squadIds = slotSquads[event.bracketSlot] || [undefined, undefined];
  const homeId = squadIds[0];
  const awayId = squadIds[1];
  const race = league.races.find(r => r.bracketSlot === event.bracketSlot);
  const structuralBye = event.round === 1 && event.awaySeed == null;
  const scores = race && homeId && awayId ? goalsBySquadId(race.games, [homeId, awayId]) : null;

  const squadEntry = (side, id) => {
    if (structuralBye && side === "away") {
      return { id: `${event.bracketSlot}-bye`, name: "Bye", winner: null, score: null, placeholder: true };
    }
    const squad = id && squadById.get(id);
    if (squad) {
      return {
        id,
        name: squad.title,
        winner: winnerFor({ race, id, homeId, structuralBye }),
        score: scores ? scores[id] : null
      };
    }
    if (event.round === 1 && event.homeSeed != null && side === "home") {
      return { id: `${event.bracketSlot}-seed${event.homeSeed}`, name: `Seed ${event.homeSeed}`, winner: null, score: null, placeholder: true };
    }
    if (event.round === 1 && event.awaySeed != null && side === "away") {
      return { id: `${event.bracketSlot}-seed${event.awaySeed}`, name: `Seed ${event.awaySeed}`, winner: null, score: null, placeholder: true };
    }
    return { id: `${event.bracketSlot}-${side}`, name: "TBD", winner: null, score: null, placeholder: true };
  };

  return {
    homeSquad: squadEntry("home", homeId),
    awaySquad: squadEntry("away", awayId),
    bracketSlot: event.bracketSlot
  };
}

function winnerFor ({ race, id, homeId, structuralBye }) {
  if (race && race.decided) {
    return race.winningSquadId === id;
  }
  if (structuralBye && id === homeId) {
    return true;
  }
  return null;
}